import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import { LoadingButton } from "../../components/ui/Button";
import { SelectInput } from "../../components/ui/form/SelectInput";
import { Badge } from "../../components/ui/Badge";
import useCheckupTherapy from "../../services/checkup/useCheckupTherapy";
import { createSignal, onMount, Show, For, createEffect } from "solid-js";
import { Button } from "@/components/shadcn/button";

export default function CheckupTherapy(props) {

  const { therapies, therapyForm, onSaveTherapy, handleTherapyInput, onFetchTherapies, statuses, addTherapy, removeTherapy, updateTherapyStatus } = useCheckupTherapy(props);
  const [isInitialized, setIsInitialized] = createSignal(false);

  onMount(() => {
    onFetchTherapies();
    // Register save function with parent component
    if (props.onRegisterSave) {
      props.onRegisterSave(onSaveTherapy);
    }
    // Set initialized flag after a brief delay to ensure props are stable
    setTimeout(() => setIsInitialized(true), 100);
  });

  const onFormChange = () => {
    const originalTherapies = props.therapies || [];
    const currentTherapies = therapyForm.therapies || [];

    // Compare therapies to see if there are changes
    const hasChanges = originalTherapies.length !== currentTherapies.length;
    if (hasChanges) {
      props.onFormChange(true);
      return;
    }

    const therapies = JSON.parse(JSON.stringify(therapyForm.therapies || []));

    let hasStatusChanges = false;
    therapies.forEach((therapy) => {
      // Get the original status from the props therapies if originalStatus is not set
      const originalTherapy = originalTherapies.find(orig => orig.therapyId === therapy.therapyId || orig.key === therapy.key);
      const actualOriginalStatus = therapy.originalStatus || originalTherapy?.status || therapy.status;
      
      // Compare with the actual original status
      if (actualOriginalStatus !== therapy.status) {
        hasStatusChanges = true;
      }
    });

    props.onFormChange(hasStatusChanges);
  }

  // Track form changes - compare with original therapies
  createEffect(() => {
    if (props.onFormChange && isInitialized()) {
      onFormChange();
    }
  });

  const content = (
    <>
      <Show when={!props.hideCard}>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Icon icon="mdi:stethoscope" class="text-gray-500 me-2" />
            Therapy
          </CardTitle>
        </CardHeader>
      </Show>
      <div class={props.hideCard ? "space-y-4" : ""}>
        {/* Add New Therapy */}
        <div class="pt-0">
          <h4 class="font-medium text-gray-900 mb-3 dark:text-gray-100">
            Therapies
          </h4>
          <div class="flex gap-2 mb-4">
            <div class="flex-1">
              <SelectInput
                label="Select Therapy"
                name="therapyId"
                form={therapyForm}
                options={therapies()}
                input={handleTherapyInput}
                placeholder="Choose a therapy..."
              />
            </div>
            <div class="flex items-end">
              <Button variant="outline" onClick={addTherapy}>
                Add Therapy
              </Button>
            </div>
          </div>
          
          {/* All Therapies List */}
          <Show
            when={
              therapyForm.therapies &&
              therapyForm.therapies.length > 0
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <For each={therapyForm.therapies}>
                {(therapy) => (
                  <div class="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border dark:bg-gray-800 dark:border-gray-700">
                    <div class="flex-1">
                      <p class="font-medium text-sm">{therapy.name}</p>
                    </div>
                    <div class="flex items-center gap-2">
                      {/* Show status dropdown only if original status was "in progress" */}
                      <Show when={therapy.originalStatus === "in progress"}>
                        <SelectInput
                          name="status"
                          value={therapy.status}
                          input={(name, value) =>
                            updateTherapyStatus(therapy.key, value)
                          }
                          options={statuses()}
                          class="w-32 text-xs"
                        />
                      </Show>

                      {/* Show current status as badge if not editable */}
                      <Show when={therapy.originalStatus !== "in progress"}>
                        <Badge variant="outline" class="text-xs capitalize">
                          {therapy.status}
                        </Badge>
                      </Show>

                      {/* Show delete button only for new therapies, preserve space for uniform design */}
                      <div class="w-8 h-8 flex items-center justify-center">
                        <Show when={therapy.status === "new"}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeTherapy(therapy.key)}
                            class="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 w-8 h-8"
                          >
                            <Icon icon="material-symbols-light:delete-outline-rounded" class="w-4 h-4" />
                          </Button>
                        </Show>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <Show
            when={
              !therapyForm.therapies ||
              therapyForm.therapies.length === 0
            }
          >
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-4">
              No therapies assigned yet
            </p>
          </Show>

          <Show when={!props.hideSaveButton}>
            <LoadingButton
              loading={props.loading}
              onClick={onSaveTherapy}
              disabled={!therapyForm.therapies}
              class="mt-4"
            >
              Save Therapies
            </LoadingButton>
          </Show>
        </div>
      </div>
    </>
  );

  return (
    <Show when={props.hideCard} fallback={<Card><CardContent class="space-y-4">{content}</CardContent></Card>}>
      {content}
    </Show>
  );
}
